import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, DownloadIcon, EyeIcon, FileTextIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { Button, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { InvoiceLinesEditor } from '../../components/admin/InvoiceLinesEditor';
import { InvoicePdfPreview } from '../../components/admin/InvoicePdfPreview';
import { InvoiceStatusBadge } from '../../components/admin/InvoiceStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { downloadInvoicePdf } from '../../utils/invoicePdf';
import { isOverdue } from '../../utils/invoice';
import { formatKr } from '../../utils/quote';
import { formatDate } from '../../utils/format';
import { invoiceStatuses } from '../../types/invoice';
import type { InvoiceLine, InvoiceStatus } from '../../types/invoice';
const ease = [0.23, 1, 0.32, 1] as const;
const SENT_WARNING = 'Denne faktura er allerede sendt. Ændringer kan betyde, at kunden har en tidligere version.';
export function InvoiceDetail() {
  const {
    invoiceId
  } = useParams();
  const navigate = useNavigate();
  const {
    invoices,
    cases,
    quotes,
    updateInvoice,
    setInvoiceStatus,
    deleteInvoice,
    authLoading,
    settings
  } = useAdminData();
  const invoice = invoices.find(entry => entry.id === invoiceId);
  const [form, setForm] = useState({
    customerName: '',
    customerAddress: '',
    customerPostalCode: '',
    customerCity: '',
    customerEmail: '',
    customerPhone: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    paymentTerms: '',
    reference: '',
    description: '',
    customerNote: '',
    internalNote: ''
  });
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [preview, setPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!invoice) return;
    setForm({
      customerName: invoice.customerName,
      customerAddress: invoice.customerAddress,
      customerPostalCode: invoice.customerPostalCode,
      customerCity: invoice.customerCity,
      customerEmail: invoice.customerEmail,
      customerPhone: invoice.customerPhone,
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      paymentTerms: invoice.paymentTerms,
      reference: invoice.reference,
      description: invoice.description,
      customerNote: invoice.customerNote,
      internalNote: invoice.internalNote
    });
    setLines(invoice.lines);
  }, [invoice?.id]);
  if (!invoice) return <Navigate to="/admin/fakturaer" replace data-id="element-929" data-mp="q2ik0zfaui6f" />;
  const linkedCase = cases.find(item => item.id === invoice.caseId);
  const linkedQuote = quotes.find(item => item.id === invoice.quoteId);
  const overdue = isOverdue(invoice);
  function change<K extends keyof typeof form>(key: K, value: string) {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    setSaved(false);
  }
  async function handleSave() {
    if (saving || !invoice) return;
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    // En sendt faktura må aldrig ændres i stilhed.
    if (invoice.status === 'Sendt' && !window.confirm(`${SENT_WARNING}\n\nVil du gemme alligevel?`)) {
      return;
    }
    setSaving(true);
    setError('');
    const result = await updateInvoice(invoice.id, {
      ...form,
      lines
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Fakturaen kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-930" data-mp="1e2snfexoc5nz">
      <Link to="/admin/fakturaer" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-931" data-mp="1e211ywxsjobf">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-932" data-mp="1nsc7gnx4i5kf" />
        Fakturaer
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-933" data-mp="102848hnuk7ty">
        <div data-id="element-934" data-mp="v46kduilk4hf">
          <p className="text-sm text-ink-muted" data-id="element-935" data-mp="v4gftxmev3ui">{invoice.invoiceNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-936" data-mp="1a17gea7tmllp">
            {invoice.customerName || 'Faktura'}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-937" data-mp="154gydhcghfxn">
            {formatKr(invoice.total)} inkl. moms · forfald {formatDate(invoice.dueDate)}
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-938" data-mp="2ggzxewsthj4b">
          <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-939" data-mp="2nzr1l3wont9b" />
          <SelectField id="faktura-status" label="Status" className="w-56" value={invoice.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setInvoiceStatus(invoice.id, event.target.value as InvoiceStatus);
          setWorking('');
          if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
        }} options={invoiceStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-940" data-mp="2t0eslpkwwa3j" />
        </div>
      </div>

      {invoice.status === 'Sendt' && <p className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-941" data-mp="31yqs6h3rai84">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-942" data-mp="3po1mpguijgdt" />
          {SENT_WARNING}
        </p>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-943" data-mp="8775bsm2x0us">
        <div className="space-y-5" data-id="element-944" data-mp="85cpgspnzyxk">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-945" data-mp="45c4ep828phj">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-946" data-mp="3vc6efckqt4ar">Kunde</h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-947" data-mp="3vdzlgsz9pqja">
              <TextField id="fa-navn" label="Navn" className="sm:col-span-2" value={form.customerName} onChange={event => change('customerName', event.target.value)} data-id="element-948" data-mp="3qck358oh5y4l" />
              <TextField id="fa-adresse" label="Adresse" className="sm:col-span-2" value={form.customerAddress} onChange={event => change('customerAddress', event.target.value)} data-id="element-949" data-mp="3cl5k51ukhwxj" />
              <TextField id="fa-postnr" label="Postnummer" value={form.customerPostalCode} onChange={event => change('customerPostalCode', event.target.value)} data-id="element-950" data-mp="2xoeguhhrw12n" />
              <TextField id="fa-by" label="By" value={form.customerCity} onChange={event => change('customerCity', event.target.value)} data-id="element-951" data-mp="2sij6a1yejtu5" />
              <TextField id="fa-mail" label="E-mail" type="email" value={form.customerEmail} onChange={event => change('customerEmail', event.target.value)} data-id="element-952" data-mp="32h8cgxgxrpuk" />
              <TextField id="fa-tlf" label="Telefon" type="tel" value={form.customerPhone} onChange={event => change('customerPhone', event.target.value)} data-id="element-953" data-mp="2ermdob4h7cn3" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-954" data-mp="1r2bjxdljmiig">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-955" data-mp="1r08ky42exf1l">
              Fakturaoplysninger
            </h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-956" data-mp="317y951p2ghs0">
              <TextField id="fa-nummer" label="Fakturanummer" value={form.invoiceNumber} onChange={event => change('invoiceNumber', event.target.value)} data-id="element-957" data-mp="319j3orbds0pv" />
              <TextField id="fa-reference" label="Reference" value={form.reference} onChange={event => change('reference', event.target.value)} data-id="element-958" data-mp="3o7dzbd9rhtqe" />
              <TextField id="fa-dato" label="Fakturadato" type="date" value={form.invoiceDate} onChange={event => change('invoiceDate', event.target.value)} data-id="element-959" data-mp="1r01k6p5yuhy" />
              <TextField id="fa-forfald" label="Forfaldsdato" type="date" value={form.dueDate} onChange={event => change('dueDate', event.target.value)} data-id="element-960" data-mp="6re7nj77fudc" />
              <TextField id="fa-betaling" label="Betalingsbetingelser" className="sm:col-span-2" value={form.paymentTerms} onChange={event => change('paymentTerms', event.target.value)} data-id="element-961" data-mp="ue9hwz5rstf7" />
            </div>
            {linkedCase && <p className="mt-5 text-sm text-ink-muted" data-id="element-962" data-mp="6ndusfc9emea">
                Sagsreference: Sag #{linkedCase.number} — {linkedCase.title}
              </p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-963" data-mp="3trtp2rgkkhkg">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-964" data-mp="2oeq8vufwrnd">Fakturalinjer</h2>
            <div className="mt-5" data-id="element-965" data-mp="3jwa7zb6nznfr">
              <InvoiceLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-966" data-mp="3jy36u4b1d3d7" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-967" data-mp="ocd8az5v33it">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-968" data-mp="9eb5ir6nmm2m">Tekst på faktura</h2>
            <TextArea id="fa-beskrivelse" label="Beskrivelse" className="mt-5" rows={4} value={form.description} onChange={event => change('description', event.target.value)} data-id="element-969" data-mp="ecme6yl7qqj9" />
            <TextArea id="fa-kundenote" label="Bemærkning til kunden" className="mt-5" rows={3} value={form.customerNote} onChange={event => change('customerNote', event.target.value)} data-id="element-970" data-mp="scx4o0im233w" />
            <TextArea id="fa-internnote" label="Intern note" hint="Vises aldrig på kundens PDF." className="mt-5" rows={3} value={form.internalNote} onChange={event => change('internalNote', event.target.value)} data-id="element-971" data-mp="1kzhw10z36f7g" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-972" data-mp="3k784s68dm4n9">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-973" data-mp="3fahobhsnt71b" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-974" data-mp="2rlyg129m0u98">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-975" data-mp="2mlss9x6gnrwf">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-976" data-mp="30lv9dh6y2ham" />
                  Gemmer …
                </> : 'Gem kladde'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPreview(true)} data-id="element-977" data-mp="fwe7ew2d60t4">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-978" data-mp="bsg92nkv4u14" />
              Forhåndsvis
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadInvoicePdf(invoice, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-979" data-mp="34parxtwayb2q">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-980" data-mp="2uwv96ud6ligs" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-981" data-mp="2zpy7tgwtfi3u" />
                  Download PDF
                </>}
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-982" data-mp="275pu4hf2eu7j">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-983" data-mp="1zni91ucl5ei5">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-984" data-mp="1qnldr9voxhe4" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-985" data-mp="1lo8ut07xwugt">
            <Button variant="secondary" size="md" disabled={working === 'sent' || invoice.status === 'Sendt'} onClick={async () => {
            setWorking('sent');
            setError('');
            const result = await setInvoiceStatus(invoice.id, 'Sendt');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-986" data-mp="1gqh1q2vk72b1">
              Marker som sendt
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'paid' || invoice.status === 'Betalt'} onClick={async () => {
            setWorking('paid');
            setError('');
            const result = await setInvoiceStatus(invoice.id, 'Betalt');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-987" data-mp="t0mp9mf5jfrb">
              Marker som betalt
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-988" data-mp="2ed7z4c5n6po4">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-989" data-mp="3ushmlidps53r" />
              Slet faktura
            </button>
          </div>
        </div>

        <aside className="space-y-5" data-id="element-990" data-mp="3isnurazjjbb">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-991" data-mp="1o2e9mge6wb2j">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-992" data-mp="1j4cqxwkoedrl">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-993" data-mp="1xsqrg0umdq4z">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-994" data-mp="1xv3hl1aj76t4">
                <dt className="text-ink-soft" data-id="element-995" data-mp="1t1igv1hjzyq8">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-996" data-mp="26tf7f13kq0ki">{formatKr(invoice.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-997" data-mp="21v3w0lxmri06">
                <dt className="text-ink-soft" data-id="element-998" data-mp="21wou2lj5lj2s">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-999" data-mp="2gqtnv0phypct">{formatKr(invoice.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1000" data-mp="2bu370b5vbpqc">
                <dt className="font-medium text-ink" data-id="element-1001" data-mp="mdkcf3de4trx">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-1002" data-mp="mfy2ext7rfsm">{formatKr(invoice.total)}</dd>
              </div>
            </dl>
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-1003" data-mp="174phzevk6n9b">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1004" data-mp="1b59eqhc7zoux">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1005" data-mp="1g5xdxqalrc08">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1006" data-mp="1q2k0cjikub4d">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1007" data-mp="1u0zjgcbjezci" />
                Åbn sag
              </Link>
            </section>}

          {linkedQuote && <section className="rounded-card border border-line bg-white p-6" data-id="element-1008" data-mp="23sdmgu2p6hae">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1009" data-mp="28tjgcv83gali">
                Tilknyttet tilbud
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1010" data-mp="23zofitinl2j">{linkedQuote.quoteNumber}</p>
              <Link to={`/admin/tilbud/${linkedQuote.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1011" data-mp="21wpg9adyhlo">
                <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1012" data-mp="3obza6opgpj49" />
                Åbn tilbud
              </Link>
            </section>}

          {invoice.customerId && <section className="rounded-card border border-line bg-white p-6" data-id="element-1013" data-mp="ktfqwborhnll">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1014" data-mp="fuupwa1oqzw3">Kunde</h2>
              <Link to={`/admin/kunder/${invoice.customerId}`} className="mt-3 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1015" data-mp="b45x8re9rwvd">
                Se kundeprofil
              </Link>
            </section>}
        </aside>
      </div>

      {preview && <InvoicePdfPreview invoice={invoice} onClose={() => setPreview(false)} data-id="element-1016" data-mp="yt7aqt1yly7i" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-faktura-titel" data-id="element-1017" data-mp="75vx2kla28c2">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1018" data-mp="3ud80lh6vyelz">
            <h2 id="slet-faktura-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1019" data-mp="uw01gu6aic44">
              Er du sikker på, at du vil slette denne faktura?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1020" data-mp="luii92xl5ub7">
              {invoice.invoiceNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1021" data-mp="36lj37mf7izy6">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1022" data-mp="2z1i9jeik0py5" />
                Fakturaen er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men
                mister henvisningen til fakturaen.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-1023" data-mp="333d5e3hpub4j">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1024" data-mp="2ff3qzmmu0pnf">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteInvoice(invoice.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Fakturaen kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/fakturaer');
          }} data-id="element-1025" data-mp="2bdi6pg7yx937">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1026" data-mp="j7a1fy71t326" />
                    Sletter …
                  </> : 'Slet faktura'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-1027" data-mp="16v1gvanvxw7o">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}